import {
  get,
  set,
  isEmpty,
  find,
  intersection as lodashIntersection,
  intersectionBy,
  filter,
  map,
  isNumber,
  isString,
  uniq,
  isArray,
  size,
  last,
} from 'lodash';
import { getUnixTime, addMinutes, fromUnixTime } from 'date-fns';
import qs from 'qs';
import {
  all,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { channel } from 'redux-saga';
import { serializeManagerName } from '../../serializers/general';
import {
  serializeClientLogs,
  serializeTicketLogs,
  extendTicketLogWithPermissions,
  serializeTicketLogsMeta,
} from '../../serializers/log';
import { parseReasons, parseSegments } from '../../serializers/reasons';
import { parseCallForLog, parseNoteForLog } from '../../serializers/updates';
import fetchData from '../../../api/fetchData';

import { safeApply, safeCall } from '../errors';

import { SET_SELECTED_CALL } from '../../actions/calls';
import {
  CREATE_NOTE,
  DELETE_NOTE_SUCCESS,
  DELETE_NOTE,
  DELETE_REASON_SUCCESS,
  DELETE_REASON,
  EDIT_NOTE_SUCCESS,
  EDIT_NOTE,
  FETCH_LOG_IF_NOT_FETCHED_YET,
  FETCH_LOGS_BY_CLIENT,
  FETCH_LOGS_BY_TICKET,
  FETCH_REASONS_SUCCESS,
  FETCH_REASONS,
  FETCH_SEGMENTS_SUCCESS,
  FETCH_SEGMENTS,
  FLAG_NOTE_SUCCESS,
  FLAG_NOTE,
  OPEN_COMMUNICATION,
  PIN_NOTE_SUCCESS,
  PIN_NOTE,
  SET_FILTERS,
  SET_LOGS,
  SET_LOGS_META,
  SET_PAGE,
  SET_RELATION,
  SET_TICKET_LOGS,
  SUBMIT_REASON_SUCCESS,
  SUBMIT_REASON,
  UPDATE_LOG,
  LOG_STATUS,
  DELETE_LOG_ATTACHMENT,
  DELETE_LOG_ATTACHMENT_SUCCESS,
} from '../../actions/log';
import {
  CREATE_TICKET_SUCCESS,
  SELECT_TICKET,
  SET_TICKET_CLIENT,
} from '../../actions/tickets';
import {
  WS_UPDATE_CALL_MESSAGE_RECEIVED,
  WS_UPDATE_CALL_REASONS_MESSAGE_RECEIVED,
  WS_UPDATE_NOTE_MESSAGE_RECEIVED,
} from '../../actions/updates';
import { TYPES as PERSON_TYPES } from '../../../consts/persons';

import { LOG_RELATIONS, CLIENT_LOG_TYPES } from '../../../consts/logs';

import { ROUTE } from '../../../consts/pages';
import WebSocketConnector from '../../../api/sockets/WebSocketConnector';
import { SOCKET_TICKET_LOG_CHANNEL } from '../../../consts/socketChannels';
import TicketLogChannelHandler from '../socketHandlers/TicketLogChannelHandler';
import { setAttachmentsInFormData } from '../utils/utils';

function* updateCommunication(payload, parser) {
  const { logs: logsState, general: generalState } = yield select(
    storage => storage
  );

  const unparsedData = get(payload, 'data');
  if (!unparsedData) {
    return null;
  }

  const parsedCommunication = parser(unparsedData);
  if (!parsedCommunication) {
    return null;
  }

  const { activeClientId } = logsState;

  if (!activeClientId) {
    return null;
  }

  if (activeClientId !== parsedCommunication.personId) {
    return null;
  }

  const { id: currentManagerId, isSupervisor } = get(
    generalState,
    'currentManager'
  );

  const isAuthor = currentManagerId === parsedCommunication.managerId;
  const createdAt = fromUnixTime(parsedCommunication.time);
  parsedCommunication.editable = isAuthor || isSupervisor;
  parsedCommunication.editableExpiresAt =
    !isSupervisor && getUnixTime(addMinutes(createdAt, 15));

  return parsedCommunication;
}

function* updateCallByWS({ payload }) {
  const communication = yield updateCommunication(payload, parseCallForLog);

  if (!communication) {
    return;
  }

  set(communication, 'call.editable', communication.editable);
  set(communication, 'call.editableExpiresAt', communication.editableExpiresAt);
  set(communication, 'call.recordUrl', communication.recordUrl);
  delete communication.editable;
  delete communication.editableExpiresAt;
  delete communication.recordUrl;

  const logsState = yield select(state => state.logs);
  const { reasons, requireNoteIds, segments } = logsState;
  const callsState = yield select(state => state.calls);
  const selectedCall = get(callsState, 'selectedCall') || null;
  const isSegmentAllowed = get(selectedCall, 'contact.isSegmentAllowed', null);

  const path = get(communication, 'call.isIncoming') ? 'incoming' : 'outgoing';
  set(communication, 'call.reasons', get(reasons, `${path}.reasons`) || []);
  set(communication, 'call.reasonGroups', get(reasons, `${path}.groups`) || []);
  set(communication, 'call.requireNoteIds', requireNoteIds.map(String));
  set(communication, 'call.segments', isSegmentAllowed && segments);

  const selectedReasonIds = get(communication, 'call.selectedReasonIds') || [];
  if (!isEmpty(selectedReasonIds)) {
    const unparsedCall = get(payload, 'data');
    const { call_reasons: unparsedReasons } = unparsedCall;
    const reasonIds = [];
    selectedReasonIds.forEach(id => {
      const unparsedReason = find(unparsedReasons, { id: +id });
      const reasonId = get(unparsedReason, 'reason_id');
      const segmentId = get(unparsedReason, 'segment_id');

      if (reasonId) {
        reasonIds.push(String(reasonId));
      }

      if (segmentId) {
        set(communication, 'call.selectedSegmentId', String(segmentId));
      }
    });
    set(communication, 'call.selectedReasonIds', reasonIds);

    const intersection = lodashIntersection(
      get(communication, 'call.requireNoteIds'),
      reasonIds
    );
    if (!isEmpty(intersection)) {
      const unparsedReasonWithDescription = find(
        unparsedReasons,
        r => r.reason_id === +intersection[0]
      );
      set(
        communication,
        'call.reasonDescription',
        get(unparsedReasonWithDescription, 'description') || ''
      );
    }
  }

  yield put({
    payload: { action: 'update', entity: communication },
    type: UPDATE_LOG,
  });
}

function* updateCallReasonsByWS({ payload }) {
  const unparsedData = get(payload, 'data');
  if (!unparsedData) {
    return;
  }

  const callId = get(payload, 'call_id');
  if (!callId) {
    return;
  }
  const serializedCallId = String(callId);

  const { logs, requireNoteIds } = yield select(state => state.logs);
  const communicationForChange = find(
    logs,
    log => get(log, 'call.id') === serializedCallId
  );
  if (!communicationForChange) {
    return;
  }

  let reasonIds = [];
  const reasonDescriptionArr = [];

  const isDeleting = get(payload, 'is_deleted');
  if (isDeleting) {
    const callReasonsIds = get(unparsedData, 'call_reason_ids');
    if (!Array.isArray(callReasonsIds)) return;
    reasonIds = filter(
      communicationForChange.call.selectedReasonIds,
      id => !callReasonsIds.includes(+id)
    );
  } else {
    const callReasons = get(unparsedData, 'call_reasons');
    if (Array.isArray(callReasons)) {
      reasonIds = map(
        callReasons,
        /* eslint-disable-next-line camelcase, no-confusing-arrow */
        ({ reason_id, description }) => {
          reasonDescriptionArr.push(description);
          return isNumber(reason_id) ? String(reason_id) : null;
        }
      );
    }
  }

  const filteredReasonIds = filter(reasonIds, isString);
  const descriptionExist = !!intersectionBy(
    filteredReasonIds,
    requireNoteIds,
    String
  ).length;
  const filteredDescriptionArr = descriptionExist && uniq(reasonDescriptionArr);
  const needDescription = !!intersectionBy(reasonIds, requireNoteIds, String)
    .length;

  const changedCall = {
    ...communicationForChange.call,
    selectedReasonIds: filteredReasonIds,
  };

  if (needDescription) {
    if (!isDeleting) {
      changedCall.reasonDescription = filteredDescriptionArr.join('');
    }
  } else {
    changedCall.reasonDescription = '';
  }

  if (isDeleting) {
    changedCall.selectedSegmentId = isEmpty(filteredReasonIds)
      ? null
      : changedCall.selectedSegmentId;
  } else {
    const segmentId = get(unparsedData, 'segment_id');
    changedCall.selectedSegmentId = isNumber(segmentId)
      ? String(segmentId)
      : null;
  }

  const changedCommunication = {
    ...communicationForChange,
    call: { ...changedCall },
  };

  yield put({
    payload: { action: 'update', entity: changedCommunication },
    type: UPDATE_LOG,
  });
}

function* updateNoteByWS({ payload }) {
  const communication = yield updateCommunication(payload, parseNoteForLog);
  if (!communication) {
    return;
  }

  set(communication, 'note.editable', communication.editable);
  set(communication, 'note.editableExpiresAt', communication.editableExpiresAt);
  delete communication.editable;
  delete communication.editableExpiresAt;

  let action = 'update';
  if (
    Array.isArray(payload.changed) &&
    payload.changed.includes('is_removed')
  ) {
    action = 'delete';
  }

  yield put({ payload: { action, entity: communication }, type: UPDATE_LOG });
}

function* isSuitableCommunication(type) {
  const logsState = yield select(({ logs }) => logs);
  const types = get(logsState, 'filters.types');
  return (isArray(types) && types.includes(type)) || !types || !size(types);
}

function* createNote({ payload }) {
  try {
    const { general: generalState, logs: logsState } = yield select(
      storage => storage
    );

    const { value, attachments, relateTo, onSuccess } = payload;
    const { activeClientId, activeTicketId } = logsState;

    if (!(activeClientId || activeTicketId)) {
      return;
    }

    const formData = new FormData();

    if (attachments.length) {
      setAttachmentsInFormData(formData, attachments);
    }

    if (relateTo === LOG_RELATIONS.CLIENT) {
      formData.append('body', value);
      formData.append('notable_type', PERSON_TYPES.CLIENT);
      formData.append(
        'manager_id',
        String(get(generalState, 'currentManager.id', null))
      );
      formData.append('notable_id', activeClientId);

      const { note } = yield safeCall(
        fetchData,
        `${process.env.API_URL}/notes`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const isSuitable = yield isSuitableCommunication('note');
      if (!isSuitable) {
        return;
      }

      const personId = get(generalState, 'currentManager.id');
      const unparsedPersonName = get(generalState, 'currentManager.name');
      const personFullname = serializeManagerName(
        'note',
        personId,
        unparsedPersonName
      );

      yield safeCall(updateNoteByWS, {
        payload: {
          type: 'note',
          data: {
            note,
            persons: [
              {
                person_id: personId && Number(personId),
                person_type: 'manager',
                full_name: personFullname,
              },
            ],
          },
        },
      });
    } else {
      formData.append('message', value);
      formData.append(
        'manager_id',
        String(get(generalState, 'currentManager.id', null))
      );
      formData.append('ticket_id', activeTicketId);

      yield safeCall(fetchData, `${process.env.API_URL}/ticket_logs/notes`, {
        method: 'POST',
        body: formData,
      });
    }

    onSuccess?.();
  } catch (error) {
    const { onError } = payload;

    onError?.();
    throw error;
  }
}

function* editNote({ payload }) {
  const { id, relateTo, value: body } = payload;

  const path =
    relateTo === LOG_RELATIONS.CLIENT
      ? `/notes/${id}`
      : `/ticket_logs/notes/${id}`;
  yield safeCall(fetchData, `${process.env.API_URL}${path}`, {
    method: 'PUT',
    body: JSON.stringify({ id, body, value_text: body }),
    headers: { 'Content-Type': 'application/json' },
  });

  yield put({ payload, type: EDIT_NOTE_SUCCESS });
}

function* pinNote({ payload }) {
  const { id, relateTo, isPinned } = payload;

  const pinParam = isPinned ? 'pin' : 'unpin';
  const path =
    relateTo === LOG_RELATIONS.CLIENT
      ? `/notes/${id}`
      : `/ticket_logs/notes/${id}`;
  const apiUrl = `${process.env.API_URL}${path}/${pinParam}`;
  yield safeCall(fetchData, apiUrl, {
    method: 'POST',
    body: JSON.stringify({ id }),
    headers: { 'Content-Type': 'application/json' },
  });

  yield put({ payload, type: PIN_NOTE_SUCCESS });
}

function* flagNote({ payload }) {
  const { id, isFlagged } = payload;

  const apiUrl = `${process.env.API_URL}/ticket_logs/notes/${id}`;
  yield safeCall(fetchData, apiUrl, {
    method: 'PUT',
    body: JSON.stringify({
      id,
      is_task: isFlagged,
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  yield put({ payload, type: FLAG_NOTE_SUCCESS });
}

function* deleteNote({ payload }) {
  const { id } = payload;

  const apiUrl = `${process.env.API_URL}/notes/${id}`;
  yield safeCall(fetchData, apiUrl, {
    method: 'DELETE',
    body: JSON.stringify({ id }),
    headers: { 'Content-Type': 'application/json' },
  });

  yield put({ payload, type: DELETE_NOTE_SUCCESS });
}

function* fetchClientLog(isPinned = false, isFirstPage = false) {
  const { logs: logsState, general: generalState } = yield select(
    storage => storage
  );

  const currentManager = get(generalState, 'currentManager') || null;
  const activeClientId = get(logsState, 'activeClientId') || null;
  const { page } = get(logsState, 'logsMeta');

  if (!activeClientId) {
    return;
  }

  const { filters } = logsState;
  const { types, size } = filters;
  const params = {
    page,
    is_pinned: isPinned,
    person_id: activeClientId,
    person_type: 'client',
    size,
  };
  if (!isEmpty(types)) {
    params.kinds = types;
  }

  const queryParams = qs.stringify(params, { arrayFormat: 'brackets' });

  const logsData = yield safeCall(
    fetchData,
    `${process.env.API_URL}/communication_logs?${queryParams}`
  );
  const isSegmentAllowed = get(logsState, 'isSegmentAllowed') || false;
  const { reasons, requireNoteIds, segments } = logsState;
  const { logs: parsedLogs, types: availableTypes } = yield safeCall(
    serializeClientLogs,
    {
      logs: logsData,
      reasons,
      requireNoteIds,
      segments: isSegmentAllowed && segments,
      currentManager,
    }
  );
  const total = get(logsData, 'meta.total') || null;

  const serializedMeta = serializeTicketLogsMeta(logsData.meta);

  yield put({
    payload: serializedMeta,
    type: SET_LOGS_META,
  });

  yield put({
    payload: {
      clientId: activeClientId,
      logs: parsedLogs,
      ...(!isPinned && isEmpty(types) && { availableTypes }),
      isNextPageLoading: isPinned,
      total,
    },
    type: SET_LOGS,
  });
}

function* fetchTicketLog(isPinned = false, isFirstPage = false) {
  const {
    logs: logsState,
    general: generalState,
    router,
  } = yield select(storage => storage);

  const activeTicketId = get(logsState, 'activeTicketId') || null;
  const currentManager = get(generalState, 'currentManager');
  const activeClientId = get(logsState, 'activeClientId') || null;
  const { page } = get(logsState, 'logsMeta');

  const isCallPage = router.location.pathname.includes(ROUTE.CALLS);

  if (!activeTicketId || isCallPage) {
    yield put({
      payload: {
        logs: [],
        total: 0,
        isNextPageLoading: false,
      },
      type: SET_LOGS,
    });
    return;
  }

  const { filters } = logsState;
  const { types, size } = filters;
  const params = {
    page,
    is_pinned: isPinned,
    ticket_id: activeTicketId,
    size,
  };
  if (!isEmpty(types)) {
    params.kinds = types;
  }

  const queryParams = qs.stringify(params, { arrayFormat: 'brackets' });

  const data = yield safeCall(
    fetchData,
    `${process.env.API_URL}/ticket_logs?${queryParams}`
  );
  const { logs: serializedLogs, types: availableTypes } = yield safeCall(
    serializeTicketLogs,
    data.ticket_logs
  );
  const ticketLogs = serializedLogs.map(ticketLog =>
    extendTicketLogWithPermissions({
      ticketLog,
      currentManager,
    })
  );
  const total = get(data, 'meta.total') || null;

  const serializedMeta = serializeTicketLogsMeta(data.meta);

  yield put({
    payload: serializedMeta,
    type: SET_LOGS_META,
  });

  yield put({
    payload: {
      clientId: activeClientId,
      logs: ticketLogs,
      ...(!isPinned && isEmpty(types) && { availableTypes }),
      isNextPageLoading: isPinned,
      total,
    },
    type: SET_LOGS,
  });

  if (!isPinned) {
    yield put({ type: SET_TICKET_LOGS });
  }
}

function* fetchLog(isFirstPage = true) {
  const { logs: logsState } = yield select(storage => storage);
  const { relateTo } = logsState;

  if (relateTo === LOG_RELATIONS.CLIENT) {
    if (isFirstPage) {
      yield safeCall(fetchClientLog, true, isFirstPage); // FETCHING PINNED
    }
    yield safeCall(fetchClientLog, false, isFirstPage);
  } else {
    if (isFirstPage) {
      yield safeCall(fetchTicketLog, true, isFirstPage); // FETCHING PINNED
    }
    yield safeCall(fetchTicketLog, false, isFirstPage);
  }
}

function* fetchLogIfNotFetchedYet() {
  const logsState = yield select(storage => storage.logs);
  const { activeClientId, fetchedForClientId } = logsState;

  if (!activeClientId) {
    return;
  }

  if (!fetchedForClientId || fetchedForClientId !== activeClientId) {
    yield safeCall(fetchLog, true);
  }
}

function* fetchReasons() {
  const reasonsData = yield safeCall(
    fetchData,
    `${process.env.API_URL}/call_reasons/reasons`
  );
  const { reasons, requireNoteIds } = yield safeCall(parseReasons, reasonsData);

  yield put({
    payload: {
      reasons,
      requireNoteIds,
    },
    type: FETCH_REASONS_SUCCESS,
  });
}

function* fetchSegments() {
  const segmentsData = yield safeCall(
    fetchData,
    `${process.env.API_URL}/call_reasons/segments`
  );
  const segments = yield safeCall(parseSegments, segmentsData);

  yield put({
    payload: { segments },
    type: FETCH_SEGMENTS_SUCCESS,
  });
}

function* submitReason({ payload }) {
  const { callId, reasons, description, segment, isUpdating } = payload;

  const apiUrl = `${process.env.API_URL}/call_reasons/multi`;
  const reasonDescription = reasons.map(() => description || '');

  const { call_reasons: callReasons } = yield safeCall(fetchData, apiUrl, {
    method: !isUpdating ? 'POST' : 'PUT',
    body: JSON.stringify({
      call_id: callId,
      reason_ids: reasons,
      reason_descriptions: reasonDescription,
      segment_id: segment,
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  const reasonIds = callReasons.map(reason => String(get(reason, 'reason_id')));

  yield put({
    payload: {
      callId,
      reasonIds,
      reasonDescription: description,
      segmentId: segment,
    },
    type: SUBMIT_REASON_SUCCESS,
  });
}

function* logStatus({ userId, status }) {
  const apiUrl = `${process.env.API_HOST_URL}${process.env.API_LOG_OPERATOR_STATUS}`;

  yield fetchData(apiUrl, {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      status,
    }),
    headers: { 'Content-Type': 'application/json' },
  });
}

function* deleteReason({ payload }) {
  const { callId, value: reasonId } = payload;
  const apiUrl = `${process.env.API_URL}/call_reasons`;

  yield safeCall(fetchData, apiUrl, {
    method: 'DELETE',
    body: JSON.stringify({
      call_id: callId,
      reason_id: reasonId,
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  yield put({
    payload: {
      callId,
      reasonId,
    },
    type: DELETE_REASON_SUCCESS,
  });
}

function* deleteLogAttachment({ payload }) {
  const { attachId, noteId, relateTo } = payload;

  const apiUrl = `${process.env.API_URL}${
    relateTo === LOG_RELATIONS.TICKET ? '/ticket_logs' : ''
  }/notes/${noteId}/attachment/${attachId}`;

  yield safeCall(fetchData, apiUrl, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  yield put({
    payload,
    type: DELETE_LOG_ATTACHMENT_SUCCESS,
  });
}

function* openCommunication({ payload }) {
  const { type, ...data } = payload;

  switch (type) {
    case CLIENT_LOG_TYPES.EMAIL: {
      const { id: chainId, isBot, ticketId } = data;
      const isValid = chainId && (isBot || ticketId);

      if (isValid) {
        const redirectUrl = isBot
          ? `${ROUTE.MANAGER_EMAILS}/${chainId}`
          : `${ROUTE.TICKETS}/${ticketId}/${chainId}`;

        yield window.open(redirectUrl, '_blank');
        break;
      }

      break;
    }

    default:
      break;
  }
}

function* watchSocketTicketLogMessage(webSocketConnector, identifier) {
  const socketChannel = channel();
  const generalState = yield select(state => state.general);
  const activeTicketChannelHandler = new TicketLogChannelHandler({
    socketChannel,
    currentManager: generalState.currentManager,
  });
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler: activeTicketChannelHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* subscribeTicketLogChannel() {
  const { activeTicketId } = yield select(state => state.tickets);

  const webSocketConnector = yield safeCall(WebSocketConnector.build);

  const identifier = {
    channel: SOCKET_TICKET_LOG_CHANNEL,
    ticket_id: activeTicketId,
  };

  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(watchSocketTicketLogMessage, webSocketConnector, identifier);
}

function* unsubscribeTicketLogTicket() {
  const { prevActiveTicketId } = yield select(state => state.tickets);

  if (!prevActiveTicketId) {
    return;
  }

  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  webSocketConnector.unsubscribeChannel({
    channel: SOCKET_TICKET_LOG_CHANNEL,
    ticket_id: prevActiveTicketId,
  });
}

export default function* () {
  yield all([
    takeEvery(CREATE_NOTE, safeApply(createNote)),
    takeEvery(DELETE_NOTE, safeApply(deleteNote)),
    takeEvery(DELETE_REASON, safeApply(deleteReason)),
    takeEvery(EDIT_NOTE, safeApply(editNote)),
    takeLatest(
      FETCH_LOG_IF_NOT_FETCHED_YET,
      safeApply(fetchLogIfNotFetchedYet)
    ),
    takeLatest(SET_TICKET_LOGS, safeApply(subscribeTicketLogChannel)),
    takeLatest(SELECT_TICKET, safeApply(unsubscribeTicketLogTicket)),
    takeEvery(FETCH_REASONS, safeApply(fetchReasons)),
    takeEvery(FETCH_SEGMENTS, safeApply(fetchSegments)),
    takeEvery(PIN_NOTE, safeApply(pinNote)),
    takeEvery(FLAG_NOTE, safeApply(flagNote)),
    takeEvery(SUBMIT_REASON, safeApply(submitReason)),
    takeEvery(WS_UPDATE_CALL_MESSAGE_RECEIVED, safeApply(updateCallByWS)),
    takeEvery(
      WS_UPDATE_CALL_REASONS_MESSAGE_RECEIVED,
      safeApply(updateCallReasonsByWS)
    ),
    takeEvery(WS_UPDATE_NOTE_MESSAGE_RECEIVED, safeApply(updateNoteByWS)),
    takeLatest(
      [
        CREATE_TICKET_SUCCESS,
        FETCH_LOGS_BY_CLIENT,
        FETCH_LOGS_BY_TICKET,
        SET_FILTERS,
        SET_RELATION,
        SET_SELECTED_CALL,
        SET_TICKET_CLIENT,
      ],
      safeApply(fetchLog, true)
    ),
    takeLatest(SET_PAGE, safeApply(fetchLog, false)),
    takeEvery(OPEN_COMMUNICATION, safeApply(openCommunication)),
    takeEvery(LOG_STATUS, safeApply(logStatus)),
    takeLatest(DELETE_LOG_ATTACHMENT, safeApply(deleteLogAttachment)),
  ]);

  yield put({ type: FETCH_REASONS });
  yield put({ type: FETCH_SEGMENTS });
}
